<script lang="ts">
  import type { PageProps } from "./$types";
  import MetaData from "$lib/MetaData.svelte";
  import Lead from "$lib/Lead.svelte";
  import LeadsBanner from "$lib/LeadsBanner.svelte";
  import TeaserSection from "$lib/TeaserSection.svelte";
  import lang from "$lib/langStore";
  import SanityPicture from "$lib/SanityPicture.svelte";
  import Cover from "$lib/Cover.svelte";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { PortableText } from "@portabletext/svelte";
  import Picture from "$lib/Picture.svelte";
  import PortableTextContent from "$lib/portableText/PortableTextContent.svelte";

  let { data }: PageProps = $props();
  let landbruk = $derived(data?.contract);
  let contract = $derived(
    data?.contract?.contract || {
      name: "Landbruk",
      description: "Ishavskrafts avtale for landbruk",
      picture: undefined,
      content: undefined,
      finePrint: undefined,
      additional_content: undefined,
    }
  );
  let heroString = $derived(data?.contract?.hero);
  let app_teaser = $derived(data?.app_teaser);
</script>

<MetaData
  data={{
    title: contract.name,
    description: contract.description,
    ...data.metaImage,
  }}
/>

{#if contract.picture}
  <Cover
    class="mt-2 mb-8"
    --cover-img-lqip={`url("${contract.picture?.lqip}")`}
    pictureFallbackBackground
  >
    <div
      class="pt-[calc(5*var(--text-h-hero))] pb-8 laptop:pr-[calc(12.5vw-var(--w-cover-x))] text-white"
    >
      <h1 class="my-0 text-h-hero font-title tablet:leading-snug">
        {heroString}
      </h1>
    </div>

    {#snippet background()}
      <div>
        {#if contract.picture?.src}
          <SanityPicture
            pictureObject={contract.picture}
            sizes={`(min-width: ${br.desktop}) 85vw, (min-width: ${br.laptop}) 90vw, 100vw`}
            class="h-full w-full object-cover laptop:rounded-2xl"
            loading="eager"
          />
        {/if}
      </div>
    {/snippet}
  </Cover>
{/if}

<div class="mx-edge-x">
  <div class="children-max-w-prose [--space-block:var(--s-6)] laptop:ml-cover-x">
    <h2 class="mb-0">{contract.name}</h2>
    <Lead class="max-w-prose">{contract.description}</Lead>

    {#if contract.content}
      <div class="text-body">
        <PortableText
          value={contract.content}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}
    {#if contract?.finePrint}
      <div class="mt-3 mb-6 children-my-1 text-base">
        <PortableText
          value={contract.finePrint}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}
  </div>
</div>

<LeadsBanner
  title="Strøm til gården og boligen?"
  subTitle="Vi kontakter deg gjerne for et skreddersydd tilbud. Fyll ut skjemaet, så hører du fra en av våre hyggelige rådgivere"
  showMessageInput={false}
  isLandbruk={true}
  customEmail="landbruk@ishavskraft.no"
/>
<div class="mx-edge-x">
  <div class="responsive-wrapper blue-box">
    {#if landbruk?.avtale_img?.src}
      <Picture
        src={landbruk.avtale_img.src}
        alt={landbruk.avtale_img.alt}
        srcset={landbruk.avtale_img.srcset}
        srcsetWebp={landbruk.avtale_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}

    {#if landbruk}
      <div class="px-4 my-6 flex-1">
        <h2>{landbruk.avtale_title ?? "Landbruk avtale"}</h2>
        <PortableText
          value={landbruk.avtale}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}
  </div>

  <div class="responsive-wrapper blue-box">
    {#if landbruk?.elbil_lading_img?.src}
      <Picture
        src={landbruk.elbil_lading_img.src}
        alt={landbruk.elbil_lading_img.alt}
        srcset={landbruk.elbil_lading_img.srcset}
        srcsetWebp={landbruk.elbil_lading_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}

    {#if landbruk}
      <div class="px-4 my-6 flex-1">
        <h2>{landbruk.elbil_lading_title ?? "Elbil lading"}</h2>
        <PortableText
          value={landbruk.elbil_lading}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}
  </div>

  <div class="responsive-wrapper blue-box flex-col-reverse">
    {#if landbruk?.varmepumpe_img?.src}
      <Picture
        src={landbruk.varmepumpe_img.src}
        alt={landbruk.varmepumpe_img.alt}
        srcset={landbruk.varmepumpe_img.srcset}
        srcsetWebp={landbruk.varmepumpe_img.srcsetWebp}
        class="object-cover w-full tablet:w-35% tablet:h-initial"
        --picture-aspect-ratio="16/9"
      />
    {/if}

    {#if landbruk}
      <div class="px-4 my-6 flex-1">
        <h2>{landbruk.varmepumpe_title ?? "Varmepumpe"}</h2>
        <PortableText
          value={landbruk.varmepumpe}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}
  </div>
</div>

{#if app_teaser}
  <TeaserSection
    class="my-12"
    circleImage={false}
    teaser={app_teaser}
    href="/app"
    lang={$lang ? "nb" : undefined}
  />
{/if}

{#if contract?.additional_content}
  <div>
    <hr class="mx-edge-x mt-12 mb-16" />

    <PortableTextContent --font-size="var(--text-sm)" content={contract.additional_content} />
  </div>
{/if}

<style>
  .blue-box {
    background-color: var(--primary-white);
    border-radius: 1rem;
    margin-block: var(--s-8);
  }

  .blue-box :global(img) {
    border-radius: 1rem 1rem 0 0;
  }

  .responsive-wrapper {
    display: flex;
    flex-direction: column;
  }

  @media (--tablet) {
    .blue-box {
      padding-inline-end: 2vw;
    }
    .blue-box :global(img) {
      border-radius: 1rem 0 0 1rem;
    }
    .responsive-wrapper {
      flex-direction: row;
      gap: 2rem;
    }
    .responsive-wrapper:nth-child(even) {
      flex-direction: row-reverse;
      padding-inline-end: 0;
    }
    .blue-box:nth-child(even) :global(img) {
      border-radius: 0 1rem 1rem 0;
    }
  }
</style>
